import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { DialogBase, NoteInfo, RentalObjectInfo, RentalObjectMovement, RequestDetails, DeviceType, ObjectTypeInfo, SmartLockInfo } from 't4core';
import { DialogService } from '../../services/dialog.service';
import * as moment from 'moment';
import { Moment } from 'moment';
import { FormControl } from '@angular/forms';
import { CreateObjectDialog } from '../../../apps/FleetManagement/dialogs/CreateObjectDialog/CreateObjectDialog';
import { ConnectLockDialog } from '../../../apps/FleetManagement/dialogs/ConnectLockDialog/ConnectLockDialog';
import * as uuid from 'uuid';


@Component({
  selector: 't4-ObjectDialog',
  templateUrl: 'ObjectDialog.html',
})
export class ObjectDialog extends DialogBase implements OnInit {
  public objectId: number;
  public object: RentalObjectInfo;
  public gpsLockId: string;
  public hasNfcTag: boolean = false;
  public objectTypes: ObjectTypeInfo[];
  public filteredObjectTypes: ObjectTypeInfo[];
  public searchText: string = '';
  public rentalEventsPageNo:number = 0;
  public isLoadingTypes: boolean = false;
  public notes: NoteInfo[] = [];
  public convertingNotes = new Set<number>();

  public isSaved: boolean = false;

  private objectLoadPromise: Promise<void> = Promise.resolve();
  private _fromDate: Moment = moment().startOf('month');
  private _toDate: Moment = moment().endOf('month');

  public get fromDate(): Moment { return this._fromDate; }
  public set fromDate(value: Moment) { this._fromDate = value; }

  public get toDate(): Moment { return this._toDate; }
  public set toDate(value: Moment) { this._toDate = value; }

  //FormControl for tab
  public selected = new FormControl(0);

  constructor(public dialogRef: MatDialogRef<ObjectDialog>, @Inject(MAT_DIALOG_DATA) public data: any, private dialogSvc: DialogService) {
    super("ObjectDialog");

    if (!data.objectId && !data.object) {
      alert("Object is missing");
      dialogRef.close();
    }

    if (data.object) {
      this.object = data.object;
      this.objectId = this.object.Id;
      this.loadNotes();
    } else {
      this.objectId = data.objectId;
      this.objectLoadPromise = this.loadObject();
    }

    if (this.object && this.object.Locks) {
      var gpsLock = this.object.Locks.find(x => x.DeviceType == DeviceType.Tracker);
      this.hasNfcTag = !!gpsLock;
      if (gpsLock) {
        this.gpsLockId = gpsLock.Id;
      }
    }

    if (data.goToTab) {
      this.selected.setValue(data.goToTab);
    }
  }

  async ngOnInit() {
    this.isLoadingTypes = true;
    await this.objectLoadPromise;
    await this.getObjectTypes();        
    this.isLoadingTypes = false;
  }

  private async loadObject() {

    this.loading.isLoading = true;
    this.object = await this.Api.get<RentalObjectInfo>("RentalObject/GetObjectInfo", { objectId: this.objectId });
    if (this.object && this.object.Locks) {
      var gpsLock = this.object.Locks.find(x => x.DeviceType == DeviceType.Tracker);
      if (gpsLock) {
        this.gpsLockId = gpsLock.Id;
      }
    }
    await this.loadNotes();
    this.loading.isLoading = false;
   
  }

  oneWayTypeIds: number[] = [340, 421, 422, 423];

  
  public async getObjectTypes() {
   
    if (!this.object) {
      return;
    }

    const objectTypeId = this.object.Type?.Id;
    if (this.oneWayTypeIds.includes(objectTypeId)) {
      this.objectTypes = await this.Api.get<ObjectTypeInfo[]>("RentalObject/GetOneWayTypes");
    }
    else { 
      this.objectTypes = await this.Api.get<ObjectTypeInfo[]>("RentalObject/GetAllTypes", { type: 0, requirePrefix: true });
      this.objectTypes = this.objectTypes.sort((x, y) => x.Title < y.Title ? -1 : 1);
    }

    this.filteredObjectTypes = this.objectTypes;

    const matchedType = this.filteredObjectTypes.find(type => type.Id === objectTypeId);
    if (matchedType) {
      this.object.Type = matchedType;
    } else {
      this.object.Type = this.objectTypes[0];
    }

  }

  public async loadNotes() {
    this.loading.isLoading = true;
    this.notes = await this.Api.get<NoteInfo[]>("RentalObject/GetNotes", { objectId: this.objectId });
    this.notes = this.notes.sort((a, b) => {
      if (!a.Action || a.Action < b.Action) return -1;
      if (!b.Action || a.Action > b.Action) return 1;

      if (a.CreatedAt > b.CreatedAt) return -1;
      if (a.CreatedAt < b.CreatedAt) return 1;

      return 0;
    });
    this.loading.isLoading = false;
  }

  public async moreMovements() {
    this.rentalEventsPageNo++;
    var data = await this.Api.get<RentalObjectMovement[]>("RentalObject/GetObjectEvent", { objectId: this.objectId, page: this.rentalEventsPageNo });


    data.map(x => this.object.Movements.push(x));

  }

  public async closeNote(note: NoteInfo) {
    if (await this.UI.confirm("turbo.core.dialogs.ObjectDialog.closeNoteHeadline", "Do you want to remove this note?", "turbo.core.dialogs.ObjectDialog.closeNoteMessage", "Are you sure you want to remove this note?")) {
      await this.Api.get<NoteInfo>("RentalObject/CloseNote", { noteId: note.Id });
      await this.loadNotes();
    }
  }

  public get canCreateServiceRequest(): boolean {
    return !!this.object && (!this.oneWayTypeIds.includes(this.object.Type.Id) || this.Settings.isInAny(['OneWayAdmin']));
  }

  public async convertNoteToServiceRequest(note: NoteInfo) {
    if (!this.canCreateServiceRequest || this.convertingNotes.has(note.Id)) return;

    this.convertingNotes.add(note.Id);
    try {
      var result = await this.Api.post<RequestDetails>("/Service/CreateRequest", null, {
        rentalObjectId: this.objectId,
        description: note.Content,
        isInsurance: false,
        disable: false,
        // A predefined ID prevents the API from merging this into an existing request.
        preDefinedID: uuid.v4()
      });

      if (result) {
        this.object.ServiceRequests = [result, ...(this.object.ServiceRequests || [])];
        try {
          await this.Api.get<NoteInfo>("RentalObject/CloseNote", { noteId: note.Id });
          await this.loadNotes();
        } finally {
          this.dialogSvc.showServiceRequest(result.Id);
        }
      }
    } finally {
      this.convertingNotes.delete(note.Id);
    }
  }

  public serviceRequestClicked(item: RequestDetails) {
    if ((this.object.Type.Id == 340 || this.object.Type.Id == 421 || this.object.Type.Id == 422 || this.object.Type.Id == 423) && !this.Settings.isInAny(['OneWayAdmin'])) return;
    this.dialogSvc.showServiceRequest(item.Id);
  }

  public async updateSettings() {
    if ((this.object.Type.Id == 340 || this.object.Type.Id == 421 || this.object.Type.Id == 422 || this.object.Type.Id == 423) && !this.Settings.isInAny(["OneWayAdmin"])) return;
    this.isSaved = false;
    await this.Api.post<boolean>("RentalObject/UpdateObject", null, {
      objectId: this.objectId,
      title: this.object.Title,
      localName: this.object.Alias,
      objectTypeId: this.object.Type.Id,
      availableOnline: this.object.AvailableOnline,
      nextInspectionDate: moment(this.object.NextInspectionDate).format('YYYY-MM-DD'),
      showInUse4Free: this.object.ShowInUse4Free,
    });
    this.isSaved = true;
  }

  public static async open(dialog: MatDialog, obj: RentalObjectInfo, id: number = null, goToTab: number = null) : Promise<Boolean> {
    var dlg = dialog.open(ObjectDialog, {
      maxHeight: "calc(100vh - 20px)",
      maxWidth: "calc(100vw - 20px)",
      minWidth: "750px",
      width: "auto",
      data: {
        object: obj,
        objectId: id,
        goToTab: goToTab
      }
    });

    return await dlg.afterClosed().toPromise();
  }

  public tabChanged(args: MatTabChangeEvent) {
   
  }

  public async loadData() {
    //Trigger ngOnChanges on child components
    this.fromDate = this.fromDate.clone();
  }

  public async openObjectEditor(object: RentalObjectInfo = null) {

    var x = await this.UI.confirm("Turbo.Core.ObjectDialog.headConfirmCopyObject", "Are you safe?", "Turbo.Core.ObjectDialog.msgConfirmCopyObject", 'Are you sure you want to copy this object and disable it?');

    if (x) {
      //Creates a copy the RentalObject as a new Object. And we set the id to 0, to indicate its a new Object in the SaveObject function.
      const newObjectToSave: RentalObjectInfo = {
        ...this.object,
        Id: 0,
        IsManual: true,
        IsDeleted: false
      };

      //Saves the new Object.
      const saveResult = await this.Api.post<boolean>("RentalObject/SaveObject", newObjectToSave, { availableOnline: newObjectToSave.AvailableOnline ?? false });


      if (!saveResult) {
        alert("Failed, try again!");
      } else {
        //Deletes the previous object.
        const deleteResult = await this.Api.post<boolean>("RentalObject/DeleteObject", null, { objectId: this.objectId });
        if (!deleteResult) {
          alert("Object created, but failed to delete previous Object!");
        }
      }
    }

  }

  getDeviceType(deviceType: DeviceType): string {
    return DeviceType[deviceType];
  }

  public async setType(value: ObjectTypeInfo) {
    this.object.Type = value;
  }

  public filterTypes() {
    this.filteredObjectTypes = this.objectTypes.filter(type => type.Title.toLowerCase().includes(this.searchText.toLowerCase()));
  }

  public resetSearch(opened: boolean) {
    if (!opened) {
      this.searchText = '';
      this.filteredObjectTypes = this.objectTypes;
    }
  }

  public async connectDevice() {
    var ret = await ConnectLockDialog.open(this.Dialog, this.objectId);
    if (ret) {
      this.loadObject();
    }
  }

  public async removeLock(lock: SmartLockInfo) {
    var x = await this.UI.confirm("Turbo.Settings.ConnectLockDialog.headDeleteDevice", "Delete device", "Turbo.Settings.ConnectLockDialog.msgDeleteDevice", "Do you want to continue deleting the device {0}?", null, [lock.Id]);
    if (x) {
      this.loading.isLoading = true;
      var ret = await this.Api.get<boolean>("Rental/DisconnectLock", { objectId: this.objectId, lockId: lock.Id });
      this.loading.isLoading = false;
      this.loadObject();
    }
    else return;
  }

  public close() {
    this.dialogRef.close(true);
  }

  public async refreshNuwielStatus() {
    var ret = await this.Api.get<boolean>("RentalObject/TriggerNuwielStatus", { deviceId: this.object.SerialNo });
    if (ret) {
      this.UI.alert("Turbo.RentalObject.NuwielTriggerSuccess", "Refresh completed", "Turbo.RentalObject.msgNuwielTriggerSuccess", "Refreshed the trailer status successfully.")
    }
    else {
      this.UI.alert("Turbo.RentalObject.NuwielTriggerFailed", "Failed!", "Turbo.RentalObject.msgNuwielTriggerFailed", "There was an error on refreshing the trailer status.")
    }
  }
}
